import AsyncStorage from "@react-native-async-storage/async-storage";
import NetInfo from "@react-native-community/netinfo";
import { useCallback, useEffect, useRef, useState } from "react";
import openhouseAPI from "../services/openhouse";
import useNetworkHealth from "./useNetworkHealth";

const QUEUE_KEY = "@pendingCheckInAttendees";
const REQUEST_TIMEOUT_MS = 15_000;
const RETRY_INTERVAL_MS = 10_000;

type PendingCheckIn = {
  id: string;
  checkInKey: string;
  attendee: any;
  createdAt: string;
};

export type CheckInSubmissionResult = {
  status: "submitted" | "queued";
};

let queueOperation = Promise.resolve();

const withQueueLock = async <T>(operation: () => Promise<T>): Promise<T> => {
  const result = queueOperation.then(operation, operation);
  queueOperation = result.then(
    () => undefined,
    () => undefined
  );
  return result;
};

const readQueue = async (): Promise<PendingCheckIn[]> => {
  const stored = await AsyncStorage.getItem(QUEUE_KEY);
  if (!stored) return [];

  const parsed = JSON.parse(stored);
  return Array.isArray(parsed) ? parsed : [];
};

const writeQueue = async (queue: PendingCheckIn[]) => {
  await AsyncStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
};

const enqueue = (submission: PendingCheckIn) =>
  withQueueLock(async () => {
    const queue = await readQueue();
    await writeQueue([...queue, submission]);
  });

const removeFromQueue = (id: string) =>
  withQueueLock(async () => {
    const queue = await readQueue();
    await writeQueue(queue.filter((submission) => submission.id !== id));
  });

const isRetryableError = (error: any) => {
  const status = error?.response?.status;
  return !status || status === 408 || status === 429 || status >= 500;
};

const sendSubmission = async (submission: PendingCheckIn) => {
  const response = await openhouseAPI.put(
    "/checkin/attendee",
    submission.attendee,
    {
      timeout: REQUEST_TIMEOUT_MS,
      headers: {
        Authorization: `checkInKey ${submission.checkInKey}`,
      },
    }
  );

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Unexpected response status: ${response.status}`);
  }
};

const createSubmissionId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const useCheckInAttendee = (checkInKey?: string | null) => {
  const network = useNetworkHealth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef<Promise<CheckInSubmissionResult> | null>(
    null
  );
  const isFlushingRef = useRef(false);

  const flushQueue = useCallback(async () => {
    // Do not gate replay on cached health state. It can remain "offline" after
    // connectivity returns; the attendee endpoint itself is authoritative.
    if (isFlushingRef.current) return;

    isFlushingRef.current = true;
    try {
      const queue = await readQueue();
      for (const submission of queue) {
        try {
          await sendSubmission(submission);
          await removeFromQueue(submission.id);
        } catch (error) {
          if (isRetryableError(error)) {
            // A connectivity/server failure will likely affect the remaining
            // queue too. Stop now and retry the batch later.
            break;
          } else {
            console.error("Queued attendee submission was rejected:", {
              submissionId: submission.id,
              status: (error as any)?.response?.status,
              response: (error as any)?.response?.data,
            });
          }
          // Keep every failed record. A transient failure can recover later,
          // and a rejected record remains available for support/recovery.
        }
      }
    } catch (error) {
      console.error("Unable to flush attendee submission queue:", error);
    } finally {
      isFlushingRef.current = false;
    }
  }, []);

  useEffect(() => {
    void flushQueue();
    const interval = setInterval(() => {
      void flushQueue();
    }, RETRY_INTERVAL_MS);
    const unsubscribe = NetInfo.addEventListener((state) => {
      if (state.isConnected && state.isInternetReachable !== false) {
        void flushQueue();
      }
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [flushQueue, network.isOffline]);

  const submitAttendee = useCallback(
    (attendee: any): Promise<CheckInSubmissionResult> => {
      if (submitInFlightRef.current) return submitInFlightRef.current;

      const operation = (async () => {
        if (!checkInKey) {
          throw new Error("A check-in key is required to submit an attendee.");
        }

        setIsSubmitting(true);
        const submission: PendingCheckIn = {
          id: createSubmissionId(),
          checkInKey,
          attendee,
          createdAt: new Date().toISOString(),
        };

        // Persist before attempting the request so a crash, reload, or loss of
        // connectivity cannot discard the completed form.
        await enqueue(submission);

        if (network.isOffline) return { status: "queued" as const };

        try {
          await sendSubmission(submission);
          await removeFromQueue(submission.id);
          return { status: "submitted" as const };
        } catch (error) {
          if (isRetryableError(error)) return { status: "queued" as const };

          console.error("Attendee submission was rejected:", {
            submissionId: submission.id,
            status: (error as any)?.response?.status,
            response: (error as any)?.response?.data,
          });
          // The form remains populated for a corrected retry. Do not leave a
          // permanently invalid request in the automatic retry queue.
          await removeFromQueue(submission.id);
          throw error;
        }
      })();

      submitInFlightRef.current = operation;
      operation.then(
        () => {
          submitInFlightRef.current = null;
          setIsSubmitting(false);
        },
        () => {
          submitInFlightRef.current = null;
          setIsSubmitting(false);
        }
      );
      return operation;
    },
    [checkInKey, network.isOffline]
  );

  return { submitAttendee, flushQueue, isSubmitting };
};

export default useCheckInAttendee;
